import React from "react";
import { Ship, Camera, Coffee, Gift, PackageCheck } from "lucide-react";
import { Reveal, Overline } from "@/components/site/Reveal";

// Custom scuba tank icon
function ScubaTankIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
      {/* Valve on top */}
      <path d="M11 2h2v2h-2z" />
      <path d="M9 4h6" />
      {/* Handle wheel */}
      <path d="M14 3.2 A2.5 2.5 0 1 1 14 4.8" />
      {/* Neck */}
      <path d="M10.5 5h3v2h-3z" />
      {/* Tank body */}
      <path d="M8 9c0-1 .8-2 2-2h4c1.2 0 2 1 2 2v10a3 3 0 0 1-3 3h-2a3 3 0 0 1-3-3V9z" />
      {/* Horizontal bands */}
      <path d="M8 13h8" />
      <path d="M8 17h8" />
    </svg>
  );
}

// Custom diving mask + snorkel icon
function MaskSnorkelIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
      {/* Mask body */}
      <path d="M4 10c0-1.5 1.2-2.5 2.7-2.5h10.6c1.5 0 2.7 1 2.7 2.5v3c0 2-1.5 3.5-3.5 3.5h-1c-1.4 0-2.6-.8-3.2-2l-.8-1.5a1 1 0 0 0-1.8 0l-.8 1.5c-.6 1.2-1.8 2-3.2 2h-1c-2 0-3.5-1.5-3.5-3.5v-3z" />
      {/* Strap */}
      <path d="M4 8.5L2 7" />
      <path d="M20 8.5L22 7" />
      {/* Snorkel tube on the right */}
      <path d="M20 10.5v-6a1.5 1.5 0 0 1 3 0V14" />
      {/* Snorkel mouthpiece */}
      <path d="M22 14h2v2h-2z" />
    </svg>
  );
}

const INCLUDED = [
  {
    icon: Ship,
    title: "Passeio de barco",
    text:
      "Navegue pelas lindas paisagens passando pela Ilha de Porto Belo, Praia do Araçá e Caixa d'Aço até a ponta de Bombinhas, em nosso ponto de mergulho no Refúgio do Estaleiro.",
  },
  {
    icon: ScubaTankIcon,
    title: "40 minutos de mergulho com cilindro",
    text: "Um instrutor particular por pessoa.",
  },
  {
    icon: MaskSnorkelIcon,
    title: "Flutuação + Máscara e Snorkel",
    text:
      "Enquanto o barco permanece no local, antes e depois do seu mergulho de cilindro, aproveite cada momento para flutuar e observar a vida marinha de cima. A roupa não te deixa afundar.",
  },
  {
    icon: PackageCheck,
    title: "Todos os equipamentos inclusos",
    text:
      "Você não precisa levar nada além da vontade de mergulhar. Cilindro, roupa de neoprene, máscara, snorkel, nadadeiras e colete — tudo já vai com você, sem nenhum custo adicional.",
  },
];

const BRINDES = [
  {
    icon: Camera,
    title: "Ganhe 10 fotos subaquáticas",
    text: "05 fotos individuais por pessoa — memórias eternizadas dessa aventura.",
  },
  {
    icon: Coffee,
    title: "Café a bordo",
    text: "Servimos um delicioso café e lanches durante o passeio.",
  },
];

function IncludedCard({ icon: Icon, title, text }) {
  return (
    <Reveal>
      <div className="relative rounded-3xl bg-white px-6 py-10 text-center shadow-[0_6px_24px_-8px_rgba(16,48,144,0.12)] ring-1 ring-ocean-mid/10 transition-shadow hover:shadow-[0_12px_36px_-12px_rgba(16,48,144,0.25)]">
        <div className="relative mx-auto mb-6 flex h-24 w-24 items-center justify-center">
          <div className="absolute inset-0 rounded-full bg-ocean-light" />
          <div className="absolute inset-2 flex items-center justify-center rounded-full bg-white shadow-inner">
            <Icon className="h-12 w-12 text-brand-navy" strokeWidth={1.5} />
          </div>
        </div>
        <h3 className="font-display text-2xl font-black uppercase tracking-tight text-brand-navy">
          {title}
        </h3>
        <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-slate-500">
          {text}
        </p>
      </div>
    </Reveal>
  );
}

function BrindeCard({ icon: Icon, title, text }) {
  return (
    <Reveal>
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-red/8 via-white to-brand-red/5 px-6 py-10 text-center shadow-[0_12px_36px_-12px_rgba(237,46,36,0.35)] ring-2 ring-brand-red/60">
        <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-brand-red px-3 py-1 text-[10px] font-black uppercase tracking-widest text-white shadow-md">
          <Gift className="h-3 w-3" /> Brinde
        </span>
        <div className="relative mx-auto mb-6 flex h-24 w-24 items-center justify-center">
          <div className="absolute inset-0 rounded-full bg-brand-red/20" />
          <div className="absolute inset-2 flex items-center justify-center rounded-full bg-white shadow-inner">
            <Icon className="h-12 w-12 text-brand-red" strokeWidth={1.5} />
          </div>
        </div>
        <h3 className="font-display text-2xl font-black uppercase tracking-tight text-brand-navy">
          {title}
        </h3>
        <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-slate-600">
          {text}
        </p>
      </div>
    </Reveal>
  );
}

export default function Inclusions() {
  return (
    <section className="relative bg-white py-20 md:py-28" data-testid="inclusions-section">
      <div className="mx-auto max-w-5xl px-5 lg:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <Overline className="text-sun">Tudo pensado para você</Overline>
          <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-[1.04] tracking-tight text-brand-navy sm:text-5xl">
            O que está incluso
          </h2>
        </Reveal>

        {/* Included items */}
        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">
          {INCLUDED.map((it) => (
            <IncludedCard key={it.title} {...it} />
          ))}
        </div>

        {/* Divider */}
        <div className="mt-16 flex items-center justify-center gap-3">
          <span className="h-px w-10 bg-brand-red/50" />
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-red/10 px-3 py-1 text-[11px] font-black uppercase tracking-widest text-brand-red">
            <Gift className="h-3 w-3" /> Brindes exclusivos
          </span>
          <span className="h-px w-10 bg-brand-red/50" />
        </div>

        {/* Brindes */}
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">
          {BRINDES.map((it) => (
            <BrindeCard key={it.title} {...it} />
          ))}
        </div>
      </div>
    </section>
  );
}
